import * as THREE from "three";
import "../vfx/haze"; // patches global fog chunks — must load before first render
import { ensureBoundsTree } from "./bvh";
import { AssetPipeline } from "./assets";
import { Clock } from "./clock";
import { bus } from "./bus";
import { Input, type InputSnapshot } from "./input";
import { ScreenShake } from "./screenshake";
import { MAP_ENVIRONMENT, SceneEnvironment } from "../world/environment";
import { terrainHeightAt } from "../world/terrain";
import { createMapTerrain, defaultRouteFor } from "../world/maps";
import { TargetRange } from "../world/targets";
import type { NavWorld } from "../world/navmesh";
import { C130 } from "../aircraft/c130";
import { CameraRig, defaultCameraRigConfig } from "../aircraft/camera-rig";
import {
  defaultFlightConfig,
  initialFlightState,
  orbitTransform,
  stepOrbit,
  type FlightState,
} from "../aircraft/flight";
import { Squad } from "../ai/squad";
import { EnemyManager } from "../ai/enemy";
import { FlakManager, altitudeBand, altitudeDispersion } from "../ai/flak";
import { ArmorManager } from "../ai/armor";
import { lineOfSight } from "../ai/perception";
import type { SoldierAsset } from "../ai/soldier-glb";

import { Chaingun } from "../weapons/chaingun";
import { Autocannon } from "../weapons/autocannon";
import { Howitzer } from "../weapons/howitzer";
import type { Weapon } from "../weapons/weapon";
import { ProjectileManager } from "../weapons/projectile";
import { ExplosionManager } from "../weapons/explosion";
import { TracerManager } from "../vfx/tracers";
import { MuzzleFlash } from "../vfx/muzzle";
import { PostProcessor, type SensorMode } from "../vfx/post";
import { ImpactManager } from "../vfx/impact";
import { ScorchManager } from "../vfx/scorch";
import { AmmoHud } from "../hud/ammo";
import { ReticleHud } from "../hud/reticle";
import { SquadStatusHud } from "../hud/squad-status";
import { MissionHud } from "../hud/mission-hud";
import { PhaseHud } from "../hud/phase-hud";
import { DebriefingHud } from "../hud/debriefing";
import { PauseMenu } from "../hud/pause";
import { OrbitHud } from "../hud/orbit-hud";
import { HintTicker } from "../hud/hints";
import { PerfTracker } from "./perf";
import { RadioHud } from "../hud/radio";
import { ThreatHud } from "../hud/threat-hud";
import { DebugPanel } from "../debug/panel";
import { Audio } from "../sfx/audio";
import { DEFAULT_LOADOUT, type LoadoutMods } from "../meta/loadout";
import type { MissionConfig } from "../mission/types";
import { loadMission } from "../mission/loader";
import escortData from "../../missions/escort-01.json";

const _origin = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _tracerEnd = new THREE.Vector3();
const _camFwd = new THREE.Vector3();
const _squadCentroid = new THREE.Vector3();
const _threatVel = new THREE.Vector3();
const _telemetryRay = new THREE.Raycaster();
// Station view ladder (playtest-tuned 2026-07-11): [1] the 25MM is the
// close-in work camera, [2] mid, [3] far-but-engaged, [4] spotter widest.
// The spotter inherited the old howitzer view.
const STATION_FOV: Record<string, number> = {
  chaingun: 30,
  autocannon: 38,
  howitzer: 46,
};
const SPOTTER_FOV = 52;
// Hold right mouse to magnify the sight this much.
const ZOOM_FACTOR = 2.2;

export type MissionResult = {
  success: boolean;
  stats: {
    shots: number;
    hits: number;
    kills: number;
    squadAlive: number;
    squadTotal: number;
    // Player ordnance events that hit Alpha (each strike = one incident)
    ffIncidents: number;
    // Named operators still standing (empty when the squad is unnamed)
    survivors: string[];
  };
};

// What the debrief screen shows beyond raw stats — supplied by the flow that
// owns meta state (run controller / test range), never computed in-engine.
export type DebriefPresentation = {
  rewardLines: string[];
  continueLabel: string;
};

type EngineCallbacks = {
  onReturnToMenu?: () => void;
  // Called once when the mission ends; the return value shapes the debrief.
  onMissionResult?: (result: MissionResult) => DebriefPresentation | undefined;
};

export class Engine {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private clock = new Clock();
  private running = false;

  private assets: AssetPipeline;
  private environment: SceneEnvironment;
  private input: Input;
  private aircraft: C130;
  private rig: CameraRig;
  private flight: FlightState;
  private flightCfg = { ...defaultFlightConfig };
  private cameraCfg = { ...defaultCameraRigConfig };
  private shaker = new ScreenShake();

  private squad: Squad;
  private enemies: EnemyManager;
  private squadFireTimer = 0;
  private orbitTarget: "squad" | "enemy" = "squad";
  // Tab cycle — restricted by the profile's Armory sensor packages on run
  // missions; the full suite elsewhere. The mission's default mode always
  // rides along (BLACKOUT hands you EO regardless of unlocks).
  private sensorModes: Array<"white-hot" | "black-hot" | "eo-green" | "daylight"> = ["daylight", "white-hot", "black-hot", "eo-green"];
  private sensorModeIdx = 0;
  private weapons: Weapon[] = [];
  private activeWeaponIdx = 0;
  private projectiles: ProjectileManager;
  private explosions: ExplosionManager;
  private tracers: TracerManager;
  private muzzle: MuzzleFlash;
  private targets: TargetRange;
  private post!: PostProcessor;
  private impacts: ImpactManager;
  private scorch: ScorchManager;
  private ammoHud: AmmoHud;
  private reticleHud: ReticleHud;
  private squadHud: SquadStatusHud;
  private missionHud: MissionHud;
  private phaseHud: PhaseHud;
  private debriefHud: DebriefingHud;
  private orbitHud!: OrbitHud;
  private radioHud!: RadioHud;
  private threatHud!: ThreatHud;
  private pauseMenu!: PauseMenu;
  private readonly missionCfg: MissionConfig;
  private trapped: Squad | null = null;
  private linkedUp = false;
  private hints!: HintTicker;
  // Station 4: the spotter scope — eyes only, weapons safe
  private spotter = false;
  private firedOnce = false;
  private perf!: PerfTracker;
  private stats = { shots: 0, hits: 0, kills: 0, ffIncidents: 0 };
  private ffCalloutCooldown = 0;
  // Aircraft survivability (docs/design/GDD.md §Survivability)
  private flak: FlakManager | null = null;
  private armor: ArmorManager | null = null;
  private hull = 100;
  private flares = 3;
  private stallTimer = 0;
  private stalled = false;
  private missionElapsed = 0; // active-phase seconds, for timed objectives
  private debug?: DebugPanel;
  private readonly callbacks: EngineCallbacks;
  // Dev-only (screenshot harness): overrides the orbit center target
  private devOrbitOverride: { x: number; z: number } | null = null;
  private readonly disposers: Array<() => void> = [];
  private stopped = false;
  private baseHudFov = this.cameraCfg.fov;
  private underFireAudioCooldown = 0;
  // Static world meshes that block infantry line-of-sight. Rebuilt on a slow
  // timer because asset-backed structures stream in after construction.
  private losOccluders: THREE.Object3D[] = [];
  // Invisible volumes (e.g. the river channel) that block navigation but
  // never render, take hits, or occlude LOS.
  private navObstacles: THREE.Mesh[] = [];
  private losRefreshTimer = 0;
  private readonly losCheck = (from: THREE.Vector3, to: THREE.Vector3): boolean =>
    lineOfSight(from, to, this.losOccluders);
  private worldRoots: THREE.Object3D[] = [];
  // Navmesh built asynchronously once the streamed world meshes settle;
  // squad/enemies steer straight lines until it lands.
  private navWorld: NavWorld | null = null;
  private navBuildStarted = false;
  private prevOccluderCount = -1;
  // Sight zoom (RMB hold), smoothed toward its target each frame
  private zoom = 1;
  private weaponFov = defaultCameraRigConfig.fov;
  // Crosshair ground range, sampled on a short timer (raycasts aren't free)
  private aimRangeM: number | null = null;
  private aimTelemetryTimer = 0;
  // Top-threat velocity tracking for the lead pip
  private readonly lastThreatPos = new THREE.Vector3();
  private readonly threatVel = new THREE.Vector3();
  private readonly leadPos = new THREE.Vector3();
  private hasThreatTrack = false;

  constructor(
    canvas: HTMLCanvasElement,
    loadout: LoadoutMods = DEFAULT_LOADOUT,
    missionCfg: MissionConfig = loadMission(escortData as Parameters<typeof loadMission>[0]),
    callbacks: EngineCallbacks = {},
    soldierAsset?: SoldierAsset,
  ) {
    this.missionCfg = missionCfg;
    this.perf = new PerfTracker(missionCfg.name);
    this.callbacks = callbacks;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene = new THREE.Scene();
    this.assets = new AssetPipeline(this.renderer);
    this.environment = new SceneEnvironment(this.renderer, this.scene, this.assets, MAP_ENVIRONMENT[missionCfg.mapId]);

    const terrain = createMapTerrain(missionCfg.mapId, this.assets);
    this.scene.add(terrain.group);
    this.applyShadows(terrain.group);
    this.perf.mark("terrain:built");

    this.targets = missionCfg.mapId === "river-valley"
      ? new TargetRange(10, [
        [-360, -45],
        [-330, -10],
        [-185, 32],
        [-145, 58],
        [-230, 188],
        [-175, 235],
        [-105, 218],
        [-85, 385],
        [-130, 430],
        [-55, 415],
      ], this.assets, [
        { path: "/models/world/kenney-car/truck.glb", scale: 5.2 },
        { path: "/models/world/kenney-car/truck-flat.glb", scale: 5.2 },
        { path: "/models/world/kenney-car/delivery.glb", scale: 5.0 },
        { path: "/models/world/kenney-car/van.glb", scale: 5.1 },
        { path: "/models/world/kenney-car/tractor-shovel.glb", scale: 5.0 },
        { path: "/models/world/kenney-industrial/detail-tank.glb", scale: 1.6 },
        { path: "/models/world/kenney-industrial/building-h.glb", scale: 0.7 },
      ])
      : new TargetRange(8);
    this.scene.add(this.targets.group);
    this.applyShadows(this.targets.group);
    this.worldRoots = [terrain.group, this.targets.group];
    this.refreshLosOccluders();

    const squadRoute = missionCfg.squadRoute && missionCfg.squadRoute.length > 0
      ? missionCfg.squadRoute
      : defaultRouteFor(missionCfg.mapId);

    this.squad = new Squad(
      squadRoute,
      missionCfg.squadSize ?? 4,
      missionCfg.squadMode,
      soldierAsset,
      missionCfg.squadDamageTakenMult ?? 1,
      missionCfg.squadNames,
      missionCfg.deathPreventions ?? 0,
    );
    this.scene.add(this.squad.group);
    this.applyShadows(this.squad.group);
    // EXTRACTION UNDER FIRE: the squad opens pinned; H sends them. Toggling
    // (not a raw flag) so the commandHold event reaches the HUD.
    if (missionCfg.squadStartHeld) this.squad.togglePlayerHold();

    // RESCUE: the trapped element — a static second squad under siege.
    // Named after its callsign so radio callouts read right; never part of
    // the roster (they're who you're rescuing, not who you brought).
    if (missionCfg.trappedSquad) {
      const t = missionCfg.trappedSquad;
      this.trapped = new Squad(
        [{ position: new THREE.Vector3(t.position[0], 0, t.position[2]), arrivalRadius: 20, label: t.label }],
        t.count,
        "hold",
        soldierAsset,
        1,
        Array.from({ length: t.count }, (_, i) => `${t.label}-${i + 1}`),
        0,
      );
      this.scene.add(this.trapped.group);
      this.applyShadows(this.trapped.group);
    }

    this.enemies = new EnemyManager(
      missionCfg.waves,
      missionCfg.enemyAnchors,
      soldierAsset,
      missionCfg.reinforcementBudget,
      missionCfg.enemyHitChanceMult ?? 1,
      missionCfg.reinforceIntervalMult ?? 1,
    );
    this.scene.add(this.enemies.group);

    if (missionCfg.flakSites && missionCfg.flakSites.length > 0) {
      this.flak = new FlakManager(missionCfg.flakSites, terrainHeightAt);
      this.scene.add(this.flak.group);
      this.applyShadows(this.flak.group);
    }
    this.flares = missionCfg.flareCount ?? 3;

    if (missionCfg.armorUnits && missionCfg.armorUnits.length > 0) {
      // Trucks/tanks hand their infantry (deploys, bailed crews) to the
      // enemy manager so all soldiers live in one system.
      this.armor = new ArmorManager(missionCfg.armorUnits, (pos, count, posture) => {
        this.enemies.spawnLoose(pos, count, posture);
      });
      this.scene.add(this.armor.group);
      this.applyShadows(this.armor.group);
    }

    this.perf.mark("actors:built");
    this.aircraft = new C130();
    this.scene.add(this.aircraft.group);
    this.applyShadows(this.aircraft.group);

    // Seed orbit center at squad start so the aircraft immediately circles the action
    const startWp = squadRoute[0]!.position;
    this.flightCfg.centerX = startWp.x;
    this.flightCfg.centerZ = startWp.z;
    // Squad-less fire missions have no friendly anchor — orbit the target set
    if ((missionCfg.squadSize ?? 4) === 0) this.orbitTarget = "enemy";

    // STORM: the ceiling drops — the safe HIGH band is unreachable — and
    // the weather thickens. New tuple, never a mutation (the default config
    // shares its range reference).
    if (missionCfg.stormAltitudeCap) {
      this.flightCfg.altitudeRange = [this.flightCfg.altitudeRange[0], missionCfg.stormAltitudeCap];
    }
    if (missionCfg.storm && this.scene.fog instanceof THREE.FogExp2) {
      this.scene.fog.density *= 1.4;
    }

    this.rig = new CameraRig(this.scene, this.cameraCfg);
    this.flight = initialFlightState(this.flightCfg);

    this.projectiles = new ProjectileManager(this.scene);
    this.explosions = new ExplosionManager(this.scene, this.targets, this.shaker);

    this.weapons = [
      new Chaingun(loadout.chaingun),
      new Autocannon(this.projectiles, loadout.autocannon),
      new Howitzer(this.projectiles, loadout.howitzer),
    ];

    this.tracers = new TracerManager(this.scene);
    this.muzzle = new MuzzleFlash(this.aircraft.gunnerPod);
    this.impacts = new ImpactManager(this.scene);
    this.scorch = new ScorchManager(this.scene);
    this.ammoHud = new AmmoHud();
    this.reticleHud = new ReticleHud();
    this.squadHud = new SquadStatusHud();
    this.missionHud = new MissionHud();
    this.phaseHud = new PhaseHud(missionCfg.mapId === "river-valley", squadRoute.map((node) => node.label));
    this.debriefHud = new DebriefingHud();
    this.orbitHud = new OrbitHud();
    this.hints = new HintTicker(missionCfg.showHints === true);
    this.radioHud = new RadioHud();
    this.threatHud = new ThreatHud();
    this.input = new Input(canvas);

    // Post-processing must be created after camera is available
    this.post = new PostProcessor(this.renderer, this.scene, this.rig.camera);
    this.perf.mark("post:built");

    // First-fire warmup: the first shot used to pay for shader compilation
    // of muzzle/tracer/explosion/impact materials (a visible hitch). Spawn
    // one of each far below the terrain and precompile the scene now.
    _origin.set(0, -600, 0);
    _tracerEnd.set(40, -600, 40);
    this.muzzle.flash();
    this.tracers.add(_origin, _tracerEnd);
    this.explosions.spawn(_origin, 3, "howitzer");
    this.impacts.spawn(_origin);
    _dir.set(0, -1, 0);
    this.projectiles.spawn(_origin, _dir, 400, 0, "autocannon", 0);
    Audio.warmup();
    this.renderer.compile(this.scene, this.rig.camera);
    this.perf.mark("warmup:done");
    // Night sorties open on the thermal; Tab cycles what the profile owns
    const defaultMode = missionCfg.sensorDefault ?? "daylight";
    if (missionCfg.sensorModes && missionCfg.sensorModes.length > 0) {
      this.sensorModes = [...missionCfg.sensorModes];
      if (!this.sensorModes.includes(defaultMode)) this.sensorModes.push(defaultMode);
    }
    this.sensorModeIdx = Math.max(0, this.sensorModes.indexOf(defaultMode));
    this.post.setMode(this.sensorModes[this.sensorModeIdx] as SensorMode);
    this.applyWeaponPresentation();

    this.pauseMenu = new PauseMenu(
      { sensitivity: this.cameraCfg.sensitivity, fov: this.cameraCfg.fov, volume: 1 },
      () => { /* resume — tick continues automatically */ },
      (s) => {
        this.cameraCfg.sensitivity = s.sensitivity;
        this.baseHudFov = s.fov;
        this.applyWeaponPresentation();
        Audio.setVolume(s.volume);
      },
    );

    // Escape → toggle pause; Tab → cycle sensor mode; H → squad Stop/Go
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.code === "Escape") this.pauseMenu.toggle();
      if (e.code === "KeyH" && !this.pauseMenu.isPaused && this.missionCfg.mission.isActive) {
        const holding = this.squad.togglePlayerHold();
        Audio.radioChatter();
        this.radioHud.show(holding
          ? "Alpha, hold position. Hold position."
          : "Alpha, continue mission. Oscar-mike.", "SPECTER 1-1");
      }
      // Space: punch flares — defeats an inbound MANPADS missile
      if (e.code === "Space" && !this.pauseMenu.isPaused && this.flares > 0) {
        e.preventDefault();
        this.flares--;
        const defeated = this.flak?.deployFlares() ?? false;
        this.muzzle.flash(); // bright pop at the airframe
        this.radioHud.show(defeated ? "Flares out — missile's chasing the sun." : "Flares, flares, flares.", "SPECTER 1-1");
      }
      if (e.code === "Tab") {
        e.preventDefault();
        // BLACKOUT: the sensor package is down — Tab is dead
        if (this.missionCfg.sensorLocked) {
          this.radioHud.show("Sensor package is down. You get the EO and you get to like it.", "SPECTER 1-1");
          return;
        }
        this.sensorModeIdx = (this.sensorModeIdx + 1) % this.sensorModes.length;
        const mode = this.sensorModes[this.sensorModeIdx] as SensorMode;
        this.post.setMode(mode);
        bus.emit("sensor:modeChanged", { mode });
      }
    };
    document.addEventListener("keydown", onKeyDown);
    this.disposers.push(() => document.removeEventListener("keydown", onKeyDown));

    // VFX reacts to weapon:hit — no weapon code should call VFX directly
    const onWeaponHit = ({ point, radius, weaponId }: { point: THREE.Vector3; radius: number; weaponId?: string }): void => {
      if (radius <= 0) {
        this.impacts.spawn(point);
      } else {
        this.explosions.spawn(point, radius, weaponId);
        this.scorch.spawn(point, radius);
      }
    };
    const onWeaponFired = (): void => { this.stats.shots++; };
    const onEnemyKilled = (): void => { this.stats.kills++; Audio.killStinger(); };
    const onEnemyFired = ({ from, to }: { from: THREE.Vector3; to: THREE.Vector3 }): void => {
      this.tracers.add(from, to, { color: 0xff7a4a, opacity: 0.9 });
    };
    const onSquadWaypointReached = ({ index }: { index: number }): void => {
      Audio.radioChatter();
      const callout = squadRoute[index]?.callout;
      if (callout) this.radioHud.show(callout);
    };
    const onDeathPrevented = ({ name }: { name: string }): void => {
      this.radioHud.show(`${name} is hit — medic's got him. He's back up!`);
      Audio.radioChatter();
    };
    bus.on("squad:deathPrevented", onDeathPrevented);
    this.disposers.push(() => bus.off("squad:deathPrevented", onDeathPrevented));

    const onSquadMemberDown = ({ name, friendlyFire }: { index: number; name?: string; friendlyFire?: boolean }): void => {
      Audio.squadUnderFire();
      if (name) {
        this.radioHud.show(friendlyFire
          ? `${name} is down — that was OUR round. Check fire!`
          : `${name} is down! I say again, ${name} is down!`);
      }
    };
    const onSquadUnderFire = (): void => {
      if (this.underFireAudioCooldown > 0) return;
      this.underFireAudioCooldown = 1.2;
      Audio.squadUnderFire();
    };
    const onMissionEnded = ({ success }: { success: boolean }): void => {
      const result: MissionResult = {
        success,
        stats: {
          ...this.stats,
          squadAlive: this.squad.aliveCount,
          squadTotal: this.squad.totalCount,
          survivors: this.squad.survivorNames(),
        },
      };
      const presentation = this.callbacks.onMissionResult?.(result)
        ?? { rewardLines: [], continueLabel: "RETURN TO MENU" };
      this.debriefHud.show(success, result.stats, presentation, () => {
        this.stop();
        this.callbacks.onReturnToMenu?.();
      });
    };
    // Anti-air: ground fire reaching up for the aircraft
    const onAircraftHit = ({ amount, source }: { amount: number; source: "aa" | "manpads" }): void => {
      const scaled = amount * (this.missionCfg.aircraftDamageTakenMult ?? 1);
      this.hull = Math.max(0, this.hull - scaled);
      this.shaker.trigger(source === "manpads" ? 3.2 : 1.2, source === "manpads" ? 0.7 : 0.3, "punch");
      Audio.squadUnderFire();
      if (this.hull <= 0) {
        this.radioHud.show("We're hit — SPECTER is going down. I say again, SPECTER is going down.", "SPECTER 1-1");
        this.missionCfg.mission.forceFail();
      } else if (source === "manpads") {
        this.radioHud.show("Impact! Hull at " + Math.round(this.hull) + " percent!", "SPECTER 1-1");
      }
    };
    const onFlakBurst = ({ from, to }: { from: THREE.Vector3; to: THREE.Vector3 }): void => {
      // A few tracers reaching up, spread around the aircraft
      for (let i = 0; i < 4; i++) {
        _tracerEnd.copy(to);
        _tracerEnd.x += (Math.random() - 0.5) * 60;
        _tracerEnd.y += (Math.random() - 0.5) * 40;
        _tracerEnd.z += (Math.random() - 0.5) * 60;
        this.tracers.add(from, _tracerEnd.clone(), { color: 0xffb35c, opacity: 0.85 });
      }
    };
    const onMissileLaunch = (): void => {
      this.radioHud.show("MISSILE LAUNCH! MANPADS, six o'clock low — FLARES!", "SPECTER 1-1");
      Audio.squadUnderFire();
    };
    bus.on("aircraft:hit", onAircraftHit);
    bus.on("flak:burst", onFlakBurst);
    bus.on("flak:missileLaunch", onMissileLaunch);
    this.disposers.push(() => bus.off("aircraft:hit", onAircraftHit));
    this.disposers.push(() => bus.off("flak:burst", onFlakBurst));
    this.disposers.push(() => bus.off("flak:missileLaunch", onMissileLaunch));

    // Armor: kill VFX, tank rounds landing on Alpha, ricochet feedback
    const onArmorDestroyed = ({ kind, position }: { kind: "tank" | "truck"; position: THREE.Vector3 }): void => {
      this.explosions.spawn(position, kind === "tank" ? 14 : 10, "howitzer");
      this.scorch.spawn(position, 10);
      this.stats.kills++;
      Audio.killStinger();
      this.radioHud.show(kind === "tank"
        ? "Armor's dead! Crew's bailing out — they're yours."
        : "Truck is burning. Watch for dismounts.");
    };
    const onTankFired = ({ hit }: { hit: boolean }): void => {
      Audio.squadUnderFire();
      if (hit) {
        this.squad.takeDamage();
        this.radioHud.show("Tank fire! We cannot take that — kill the armor!");
      }
    };
    const onRicochet = (): void => {
      if (this.ffCalloutCooldown <= 0) {
        this.ffCalloutCooldown = 5;
        this.radioHud.show("Your two-five is bouncing off that armor. Go guns two or three.", "SPECTER 1-1");
      }
    };
    bus.on("armor:destroyed", onArmorDestroyed);
    bus.on("armor:tankFired", onTankFired);
    bus.on("armor:ricochet", onRicochet);
    this.disposers.push(() => bus.off("armor:destroyed", onArmorDestroyed));
    this.disposers.push(() => bus.off("armor:tankFired", onTankFired));
    this.disposers.push(() => bus.off("armor:ricochet", onRicochet));

    bus.on("weapon:hit", onWeaponHit);
    bus.on("weapon:fired", onWeaponFired);
    bus.on("enemy:killed", onEnemyKilled);
    bus.on("enemy:fired", onEnemyFired);
    bus.on("squad:waypointReached", onSquadWaypointReached);
    bus.on("squad:memberDown", onSquadMemberDown);
    bus.on("squad:underFire", onSquadUnderFire);
    bus.on("mission:ended", onMissionEnded);
    this.disposers.push(() => bus.off("weapon:hit", onWeaponHit));
    this.disposers.push(() => bus.off("weapon:fired", onWeaponFired));
    this.disposers.push(() => bus.off("enemy:killed", onEnemyKilled));
    this.disposers.push(() => bus.off("enemy:fired", onEnemyFired));
    this.disposers.push(() => bus.off("squad:waypointReached", onSquadWaypointReached));
    this.disposers.push(() => bus.off("squad:memberDown", onSquadMemberDown));
    this.disposers.push(() => bus.off("squad:underFire", onSquadUnderFire));
    this.disposers.push(() => bus.off("mission:ended", onMissionEnded));

    if (import.meta.env.DEV) {
      this.debug = new DebugPanel({ flight: this.flightCfg, camera: this.cameraCfg });
      // Dev-only handle for the screenshot harness (scripts/shot.mjs) —
      // synthetic pointer-lock mouse deltas don't reach the rig in headless
      // Chromium, so the harness aims the camera through this instead.
      (window as unknown as {
        __c130?: { aim: (dx: number, dy: number) => void; scene: THREE.Scene; setOrbitOverride: (x: number, z: number) => void };
      }).__c130 = {
        aim: (dx: number, dy: number) => this.rig.aim(dx, dy),
        scene: this.scene,
        setOrbitOverride: (x: number, z: number) => { this.devOrbitOverride = { x, z }; },
      };
    }

    this.handleResize();
    const onResize = (): void => this.handleResize();
    window.addEventListener("resize", onResize);
    this.disposers.push(() => window.removeEventListener("resize", onResize));
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.clock.reset();
    this.missionCfg.mission.start();
    void this.environment.initialize();
    Audio.startEngineDrone();
    Audio.startWindAmbience();
    requestAnimationFrame(this.tick);
    bus.emit("engine:started");
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.running = false;
    Audio.stopEngineDrone();
    this.input.dispose();
    this.tracers.dispose();
    this.projectiles.dispose();
    this.explosions.dispose();
    this.impacts.dispose();
    this.scorch.dispose();
    this.environment.dispose();
    this.enemies.dispose();
    this.navWorld?.dispose();
    this.navWorld = null;
    this.reticleHud.dispose();
    this.hints.dispose();
    this.perf.flush();
    this.squadHud.dispose();
    this.phaseHud.dispose();
    this.orbitHud.dispose();
    this.radioHud.dispose();
    this.threatHud.dispose();
    this.pauseMenu.dispose();
    this.debriefHud.dispose();
    this.debug?.dispose();
    for (const dispose of this.disposers.splice(0)) dispose();
    bus.emit("engine:stopped");
  }

  private tick = (): void => {
    if (!this.running) return;
    if (this.pauseMenu.isPaused) {
      requestAnimationFrame(this.tick);
      return;
    }
    const dt = this.clock.step();
    if (this.underFireAudioCooldown > 0) this.underFireAudioCooldown = Math.max(0, this.underFireAudioCooldown - dt);
    if (this.ffCalloutCooldown > 0) this.ffCalloutCooldown = Math.max(0, this.ffCalloutCooldown - dt);
    const snap = this.input.consume();

    this.updateFlight(snap, dt);
    this.updateCamera(snap, dt);
    this.updateWeaponSwitch(snap);
    this.updateFire(snap);
    this.projectiles.update(dt, (point, blastRadius, hitObj, weaponId, blastDamage) => {
      bus.emit("weapon:hit", { point, radius: blastRadius, weaponId });
      const killedOrWounded = this.enemies.hitInRadius(point, blastRadius, blastDamage);
      if (killedOrWounded) { this.reticleHud.flash(); this.stats.hits++; }
      if (hitObj?.userData["isTarget"]) this.reticleHud.flash();
      const flakKills = this.flak?.hitInRadius(point, blastRadius) ?? 0;
      if (flakKills > 0) { this.reticleHud.flash(); this.stats.hits++; }
      const armorKills = this.armor?.hitInRadius(point, blastRadius, weaponId) ?? [];
      if (armorKills.length > 0) { this.reticleHud.flash(); this.stats.hits++; }
      this.checkFriendlyFire(point, blastRadius);
    });
    this.explosions.update(dt);
    this.impacts.update(dt);
    this.tracers.update(dt);
    this.muzzle.update(dt);
    this.targets.update(dt);
    this.losRefreshTimer -= dt;
    if (this.losRefreshTimer <= 0) {
      this.losRefreshTimer = 5;
      this.refreshLosOccluders();
      this.maybeBuildNavWorld();
    }
    this.squad.update(dt, (node) => this.enemies.hasActiveThreatNear(node.position, Squad.tacticalHoldRadius(node)));
    this.enemies.update(dt, this.groundElements(), this.losCheck);
    this.updateLinkUp();
    if (this.flak && this.missionCfg.mission.isActive) {
      this.aircraft.group.getWorldPosition(_origin);
      this.flak.update(dt, _origin, this.flight.altitude,
        { x: this.flightCfg.centerX, z: this.flightCfg.centerZ },
        this.orbitTarget === "enemy");
    }
    if (this.armor && this.missionCfg.mission.isActive) {
      this.armor.update(dt, this.groundElements().flatMap((s) => s.memberPositions()));
    }
    this.updateStall(dt);
    this.updateSquadCounterFire(dt);
    this.updateIFF();

    for (const w of this.weapons) w.update(dt);

    if (this.missionCfg.mission.isActive) this.missionElapsed += dt;
    const msnSnap = {
      squadAlive: this.squad.aliveCount,
      squadTotal: this.squad.totalCount,
      squadCompleted: this.squad.completed,
      enemiesAlive: this.enemies.aliveCount,
      totalEnemiesSpawned: this.enemies.totalEnemiesSpawned,
      elapsed: this.missionElapsed,
      primariesAlive: (this.armor?.aliveCount ?? 0) + (this.flak?.aliveCount ?? 0),
      primariesTotal: (this.armor?.totalCount ?? 0) + (this.flak?.totalCount ?? 0),
      convoyEscaped: this.armor?.escapedCount ?? 0,
      trappedAlive: this.trapped?.aliveCount ?? 0,
      linkedUp: this.linkedUp,
    };
    this.missionCfg.mission.update(dt, msnSnap);

    this.updateAimTelemetry(dt);

    this.reticleHud.update(dt);
    this.radioHud.update(dt);
    const threatPos = this.enemies.topThreatPosition();
    this.threatHud.update(dt, this.rig.camera, threatPos, this.computeLeadPoint(dt, threatPos));
    const speedPct = ((this.flightCfg.angularSpeed - 0.04) / (0.45 - 0.04)) * 100;
    this.orbitHud.updateInstruments({
      altitude: this.flight.altitude,
      speedPct,
      altBand: altitudeBand(this.flight.altitude),
      hull: this.hull,
      flares: this.flares,
      missileInbound: this.flak?.missileInbound ?? false,
      stalled: this.stalled,
      flakThreat: this.flak !== null && this.flak.aliveCount > 0,
    });
    const activeSpeed = this.weapons[this.activeWeaponIdx]?.projectileSpeed;
    this.ammoHud.update(this.weapons, this.activeWeaponIdx, {
      rangeM: this.aimRangeM,
      altitudeM: this.flight.altitude,
      tofS: activeSpeed && this.aimRangeM !== null ? this.aimRangeM / activeSpeed : null,
      zoomFactor: this.zoom,
    }, this.spotter);
    this.squadHud.update(this.squad, dt);
    this.missionHud.update(this.missionCfg.mission, msnSnap);
    this.phaseHud.update(dt);
    this.hints.update(dt);
    this.perf.frame(dt * 1000);
    this.perf.setPhase(this.missionCfg.mission.isActive ? "active" : "pre-active");

    this.post.render(dt);
    requestAnimationFrame(this.tick);
  };

  private updateFlight(snap: InputSnapshot, dt: number): void {
    // Q = orbit squad, E = orbit enemy cluster
    if (snap.keys["KeyQ"] && this.orbitTarget !== "squad") {
      this.orbitTarget = "squad";
      bus.emit("orbit:targetChanged", { target: this.orbitTarget });
    } else if (snap.keys["KeyE"] && this.orbitTarget !== "enemy") {
      this.orbitTarget = "enemy";
      bus.emit("orbit:targetChanged", { target: this.orbitTarget });
    }

    // Lerp orbit center toward active target. "Enemy" includes armor —
    // convoys and tanks pull the orbit like infantry clusters do.
    const enemyPositions = this.orbitTarget === "enemy"
      ? [...this.enemies.alivePositions(), ...(this.armor?.alivePositions() ?? [])]
      : [];
    const targetPositions = enemyPositions.length > 0
      ? enemyPositions
      : this.squad.memberPositions();

    if (import.meta.env.DEV && this.devOrbitOverride) {
      _squadCentroid.set(this.devOrbitOverride.x, 0, this.devOrbitOverride.z);
      const alpha = Math.min(8.0 * dt, 1); // snap fast — headless harness runs few frames
      this.flightCfg.centerX += (_squadCentroid.x - this.flightCfg.centerX) * alpha;
      this.flightCfg.centerZ += (_squadCentroid.z - this.flightCfg.centerZ) * alpha;
    } else if (targetPositions.length > 0) {
      _squadCentroid.set(0, 0, 0);
      for (const p of targetPositions) _squadCentroid.add(p);
      _squadCentroid.divideScalar(targetPositions.length);
      const alpha = Math.min(0.4 * dt, 1);
      this.flightCfg.centerX += (_squadCentroid.x - this.flightCfg.centerX) * alpha;
      this.flightCfg.centerZ += (_squadCentroid.z - this.flightCfg.centerZ) * alpha;
    }

    // W/S nudge orbit speed; clamped to [0.04, 0.45] rad/s
    if (snap.keys["KeyW"]) this.flightCfg.angularSpeed = Math.min(0.45, this.flightCfg.angularSpeed + 0.18 * dt);
    if (snap.keys["KeyS"]) this.flightCfg.angularSpeed = Math.max(0.04, this.flightCfg.angularSpeed - 0.18 * dt);

    // Shift: 3× orbit speed boost to reposition quickly
    const boost = snap.keys["ShiftLeft"] || snap.keys["ShiftRight"] ? 3 : 1;
    const baseCfg = { ...this.flightCfg, angularSpeed: this.flightCfg.angularSpeed * boost };

    const radiusDir = (snap.keys["KeyD"] ? 1 : 0) - (snap.keys["KeyA"] ? 1 : 0);
    const altitudeDir = (snap.keys["KeyX"] ? 1 : 0) - (snap.keys["KeyZ"] ? 1 : 0);
    this.flight = stepOrbit(this.flight, { radiusDir, altitudeDir }, dt, baseCfg);
    const xf = orbitTransform(this.flight, this.flightCfg);
    this.aircraft.applyTransform(xf.position, xf.heading, xf.bank);
  }

  private readonly _podWorld = new THREE.Vector3();
  private readonly _shakeVec = new THREE.Vector3();

  private updateCamera(snap: InputSnapshot, dt: number): void {
    // RMB hold = sight zoom; mouse input scales down with zoom so tracking
    // stays controllable at magnification.
    const zoomTarget = snap.pointerLocked && snap.mouseButtons[2] === true ? ZOOM_FACTOR : 1;
    this.zoom += (zoomTarget - this.zoom) * (1 - Math.exp(-10 * dt));
    this.cameraCfg.fov = THREE.MathUtils.clamp(this.weaponFov / this.zoom, 16, 80);

    if (snap.pointerLocked) this.rig.aim(snap.mouseDX / this.zoom, snap.mouseDY / this.zoom);
    this.rig.camera.fov = this.cameraCfg.fov;
    const shake = this.shaker.update(dt);
    this._shakeVec.set(shake.x, shake.y, shake.z);
    this.aircraft.gunnerPod.getWorldPosition(this._podWorld);
    // Camera always faces orbit center: heading = PI/2 - angle.
    // (Aircraft nose uses angle + PI/2 for tangential flight — camera is independent.)
    const heading = Math.PI / 2 - this.flight.angle;
    this.rig.updateTransform(this._podWorld, heading, this._shakeVec, dt);
    this.rig.resize(window.innerWidth / window.innerHeight);
  }

  private updateWeaponSwitch(snap: InputSnapshot): void {
    const stations = this.weapons.length + 1; // guns + the spotter scope
    const prev = this.spotter ? this.weapons.length : this.activeWeaponIdx;
    let station = prev;
    if (snap.keys["Digit1"]) station = 0;
    else if (snap.keys["Digit2"]) station = 1;
    else if (snap.keys["Digit3"]) station = 2;
    else if (snap.keys["Digit4"]) station = this.weapons.length;
    else if (snap.scrollDelta !== 0) {
      station = ((station + Math.sign(snap.scrollDelta)) % stations + stations) % stations;
    }
    if (station === prev) return;
    this.spotter = station === this.weapons.length;
    if (!this.spotter) this.activeWeaponIdx = station;
    this.applyWeaponPresentation();
  }

  private updateFire(snap: InputSnapshot): void {
    if (this.spotter) return; // eyes only — weapons safe on station 4
    const firing = snap.pointerLocked && snap.mouseButtons[0] === true;
    if (!firing) return;

    const weapon = this.weapons[this.activeWeaponIdx]!;
    this.rig.camera.getWorldPosition(_origin);  // fire from camera, not pod root (eliminates parallax)
    this.rig.camera.getWorldDirection(_dir);

    // Altitude costs accuracy: low and slow shoots tight, high and safe
    // sprays (docs/design/GDD.md §Altitude). Stall buffet doubles it.
    const disp = altitudeDispersion(this.flight.altitude) * (this.stalled ? 2 : 1) * (this.missionCfg.dispersionMult ?? 1);
    if (disp > 0) {
      _dir.x += (Math.random() - 0.5) * disp;
      _dir.y += (Math.random() - 0.5) * disp;
      _dir.z += (Math.random() - 0.5) * disp;
      _dir.normalize();
    }

    const result = weapon.fire(_origin, _dir, this.scene);
    if (!result.fired) return;
    if (!this.firedOnce) {
      this.firedOnce = true;
      this.perf.mark("first-fire");
    }

    this.muzzle.flash();
    switch (weapon.id) {
      case "chaingun":   this.shaker.trigger(0.55, 0.08, "punch"); break;
      case "autocannon": this.shaker.trigger(1.4,  0.22, "punch"); break;
      case "howitzer":   this.shaker.trigger(2.8,  0.55, "punch"); break;
    }

    if (result.hitPoint) {
      this.tracers.add(_origin, result.hitPoint);
      bus.emit("weapon:hit", { point: result.hitPoint, radius: 0, weaponId: weapon.id });
      if (result.hitObject?.userData["isTarget"]) {
        this.targets.hit(result.hitObject);
        this.reticleHud.flash();
      } else if (result.hitObject?.userData["isEnemy"]) {
        this.enemies.hit(result.hitObject);
        this.reticleHud.flash();
        this.stats.hits++;
      } else if (result.hitObject?.userData["isFlakSite"] && this.flak?.hitObject(result.hitObject)) {
        this.reticleHud.flash();
        this.stats.hits++;
      } else if (result.hitObject?.userData["isArmor"] && this.armor) {
        const outcome = this.armor.hitObject(result.hitObject, weapon.id);
        if (outcome === "hit" || outcome === "destroyed") { this.reticleHud.flash(); this.stats.hits++; }
        // ricochet: impact spark only — the 25mm cannot hurt armor
      } else {
        // Chaingun: fragmentary splash — round landing near enemies still damages them
        const splashKills = this.enemies.hitInRadius(result.hitPoint, 2.5);
        if (splashKills) { this.reticleHud.flash(); this.stats.hits++; }
        this.checkFriendlyFire(result.hitPoint, 2.0);
      }
    } else {
      _tracerEnd.copy(_origin).addScaledVector(_dir, 2500);
      this.tracers.add(_origin, _tracerEnd);
    }
  }

  // Below minimum airspeed the airframe shudders and the crew forces the
  // throttles forward — loitering at a crawl is not free vantage.
  private updateStall(dt: number): void {
    if (this.flightCfg.angularSpeed < 0.065) {
      this.stallTimer += dt;
      if (this.stallTimer > 1.2 && !this.stalled) {
        this.stalled = true;
        this.radioHud.show("Airspeed! Airspeed! Push it up.", "SPECTER 1-1");
      }
    } else {
      this.stallTimer = 0;
      this.stalled = false;
    }
    if (this.stalled) {
      // Forced correction + buffet
      this.flightCfg.angularSpeed = Math.min(0.45, this.flightCfg.angularSpeed + 0.06 * dt);
      this.shaker.trigger(0.5, 0.12, "rumble");
      if (this.flightCfg.angularSpeed >= 0.09) this.stalled = false;
    }
  }

  // Player ordnance vs Alpha. Every strike that touches a squad member is a
  // friendly-fire incident: damage lands, scoring is docked, the FCO barks.
  // Every friendly element enemies may engage. After link-up the trapped
  // element folds into Alpha (fiction: they move as one) and leaves the map.
  private groundElements(): Squad[] {
    return this.trapped && !this.linkedUp ? [this.squad, this.trapped] : [this.squad];
  }

  private static readonly LINK_UP_RADIUS = 40;

  private updateLinkUp(): void {
    if (!this.trapped || this.linkedUp || !this.missionCfg.mission.isActive) return;
    // VIP escort: no fold-in — the package stays on the ground and killable
    if (this.missionCfg.trappedSquad?.holdTogether) return;
    if (this.trapped.aliveCount === 0) return; // nothing left to link up with
    const trappedPos = this.trapped.group.position;
    const near = this.squad.memberPositions().some((p) =>
      Math.hypot(p.x - trappedPos.x, p.z - trappedPos.z) < Engine.LINK_UP_RADIUS
      || this.trapped!.memberPositions().some((t) => p.distanceTo(t) < Engine.LINK_UP_RADIUS));
    if (!near) return;
    this.linkedUp = true;
    this.trapped.group.visible = false;
    this.radioHud.show("Link-up! They're with us — moving as one. Get us home, Specter.");
    bus.emit("squad:linkUp", {});
  }

  private checkFriendlyFire(point: THREE.Vector3, radius: number): void {
    if (radius <= 0) return;
    const hits = this.squad.hitInRadius(point, radius)
      + (this.trapped && !this.linkedUp ? this.trapped.hitInRadius(point, radius) : 0);
    if (hits === 0) return;
    this.stats.ffIncidents++;
    bus.emit("squad:friendlyFire", { hits });
    if (this.ffCalloutCooldown <= 0) {
      this.ffCalloutCooldown = 4;
      this.radioHud.show("CHECK FIRE! CHECK FIRE! Danger close on Alpha!");
      Audio.squadUnderFire();
    }
  }

  private updateSquadCounterFire(dt: number): void {
    if (this.enemies.aliveCount === 0 || this.squad.aliveCount === 0) return;
    this.squadFireTimer -= dt;
    if (this.squadFireTimer <= 0) {
      // Fire rate scales with squad size; low hit chance — player is the deciding factor
      this.squadFireTimer = 3.0 / this.squad.aliveCount;
      if (Math.random() < 0.20 * (this.missionCfg.squadFireEffectMult ?? 1)) {
        this.enemies.hitNearest(this.squad.memberPositions(), this.losCheck);
      }
    }
  }

  private updateIFF(): void {
    this.rig.camera.getWorldPosition(_origin);
    this.rig.camera.getWorldDirection(_camFwd);
    if (this.squad.checkIFF(_origin, _camFwd)) {
      this.reticleHud.setIFF("friendly");
    } else if (this.enemies.checkEnemyIFF(_origin, _camFwd)) {
      this.reticleHud.setIFF("enemy");
    } else {
      this.reticleHud.setIFF("neutral");
    }
  }

  // Sample the ground range under the crosshair on a short timer — a 10Hz
  // readout is indistinguishable from per-frame for the HUD, at a tenth of
  // the raycast cost.
  private updateAimTelemetry(dt: number): void {
    this.aimTelemetryTimer -= dt;
    if (this.aimTelemetryTimer > 0) return;
    this.aimTelemetryTimer = 0.1;

    this.rig.camera.getWorldPosition(_origin);
    this.rig.camera.getWorldDirection(_dir);
    _telemetryRay.set(_origin, _dir);
    _telemetryRay.far = 4000;
    _telemetryRay.firstHitOnly = true; // BVH: nearest hit only
    const hits = _telemetryRay.intersectObjects(this.losOccluders, false);
    this.aimRangeM = hits.length > 0 ? hits[0]!.distance : null;
  }

  // Where to aim so the active weapon's shell and the moving top threat
  // arrive together. Null when there is nothing to lead: hitscan weapon,
  // stationary target, or negligible flight time.
  private computeLeadPoint(dt: number, threatPos: THREE.Vector3 | null): THREE.Vector3 | null {
    if (!threatPos || dt <= 0) {
      this.hasThreatTrack = false;
      return null;
    }

    if (this.hasThreatTrack) {
      const jump = threatPos.distanceTo(this.lastThreatPos);
      if (jump > 15) {
        this.threatVel.set(0, 0, 0); // top threat switched — restart the track
      } else {
        _threatVel.copy(threatPos).sub(this.lastThreatPos).divideScalar(dt);
        this.threatVel.lerp(_threatVel, 1 - Math.exp(-6 * dt));
      }
    }
    this.lastThreatPos.copy(threatPos);
    this.hasThreatTrack = true;

    const speed = this.weapons[this.activeWeaponIdx]?.projectileSpeed;
    if (!speed) return null;
    this.rig.camera.getWorldPosition(_origin);
    const tof = _origin.distanceTo(threatPos) / speed;
    if (tof < 0.12) return null;
    const velSq = this.threatVel.lengthSq();
    if (velSq < 0.25 || velSq > 100) return null; // stationary, or a glitchy spike

    return this.leadPos.copy(threatPos).addScaledVector(this.threatVel, tof);
  }

  // Flat list of static meshes for infantry LOS raycasts. InstancedMesh
  // (vegetation) is excluded — per-instance raycasts are too costly without a
  // BVH — as are conformal cosmetic surfaces tagged losTransparent.
  private refreshLosOccluders(): void {
    const list: THREE.Object3D[] = [];
    const navObstacles: THREE.Mesh[] = [];
    for (const root of this.worldRoots) {
      root.traverse((obj) => {
        if (!(obj instanceof THREE.Mesh) || obj instanceof THREE.InstancedMesh) return;
        if (obj.userData["navObstacle"]) { navObstacles.push(obj); return; }
        if (obj.userData["isVFX"] || obj.userData["losTransparent"]) return;
        ensureBoundsTree(obj);
        list.push(obj);
      });
    }
    this.losOccluders = list;
    this.navObstacles = navObstacles;
  }

  // Kick off the async navmesh build once the streamed world meshes have
  // settled (occluder count stable across two refreshes). One build per
  // mission; agents fall back to straight lines until it resolves.
  private maybeBuildNavWorld(): void {
    if (this.navBuildStarted) {
      return;
    }
    const count = this.losOccluders.length;
    if (count > 0 && count === this.prevOccluderCount) {
      this.navBuildStarted = true;
      const meshes = [
        ...this.losOccluders.filter((o): o is THREE.Mesh => o instanceof THREE.Mesh),
        ...this.navObstacles,
      ];
      // Dynamic import keeps the recast WASM chunk (~220KB gzip) off the
      // initial page load — it's only needed once a mission is running.
      void import("../world/navmesh")
        .then(({ NavWorld }) => NavWorld.create(meshes))
        .then((nav) => {
          if (!nav) return;
          if (this.stopped) { nav.dispose(); return; }
          this.navWorld = nav;
          this.squad.setNavWorld(nav);
          this.enemies.setNavWorld(nav);
        });
    }
    this.prevOccluderCount = count;
  }

  // Traverse a group and enable cast/receive shadows on all non-VFX meshes.
  // VFX objects (smoke, tracers, scorch) are excluded via userData["isVFX"].
  private applyShadows(root: THREE.Object3D): void {
    root.traverse((obj) => {
      if (obj.userData["isVFX"]) return;
      if (obj instanceof THREE.Mesh || obj instanceof THREE.InstancedMesh || obj instanceof THREE.SkinnedMesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
      }
    });
  }

  private handleResize(): void {
    const { innerWidth: w, innerHeight: h } = window;
    this.renderer.setSize(w, h, false);
    this.post?.resize(w, h);
    this.rig.resize(w / h);
  }

  private applyWeaponPresentation(): void {
    if (this.spotter) {
      this.weaponFov = SPOTTER_FOV;
      this.reticleHud.setWeapon("spotter");
    } else {
      const weapon = this.weapons[this.activeWeaponIdx];
      if (!weapon) return;
      // Dev fov slider shifts the whole ladder together
      const debugShift = this.baseHudFov - defaultCameraRigConfig.fov;
      this.weaponFov = THREE.MathUtils.clamp(
        (STATION_FOV[weapon.id] ?? SPOTTER_FOV) + debugShift, 24, 80);
      this.reticleHud.setWeapon(weapon.id as "chaingun" | "autocannon" | "howitzer");
    }
    this.cameraCfg.fov = THREE.MathUtils.clamp(this.weaponFov / this.zoom, 16, 80);
    this.rig.camera.fov = this.cameraCfg.fov;
    this.rig.camera.updateProjectionMatrix();
  }
}
